import { useEffect, useState } from "react";
import { useAuth } from "../../../context";
import { getObjectsWithParams } from "../../../api/Api";
import {
  Box,
  Paper,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import WarningIcon from "@mui/icons-material/Warning";
import PersonIcon from "@mui/icons-material/Person";
import { red } from "@mui/material/colors";

const gridItemCenterStyle = {
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "25px 5px",
  borderRadius: "10px",
  color: "white",
};

const warningStyle = {
  ...gridItemCenterStyle,
  cursor: "pointer",
  backgroundColor: red[500],
  border: "2px solid #d32f2f",
  boxShadow: "0px 4px 10px rgba(211, 47, 47, 0.5)",
  "&:hover": {
    backgroundColor: red[700],
  },
};

export default function EmergencyOverview() {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token || "";
  const theme = useTheme();
  const [option, setOption] = useState("daily");
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        setLoading(true);
        const today = new Date().toISOString().split("T")[0];
        const yesterday = new Date(Date.now() - 24 * 60 * 60 * 1000)
          .toISOString()
          .split("T")[0];

        // Fetch today's visits
        const todayVisits = await getObjectsWithParams(
          "emergency-visits",
          { arrival_time__gte: today },
          token
        );

        // Fetch yesterday's visits for comparison
        const yesterdayVisits = await getObjectsWithParams(
          "emergency-visits",
          {
            arrival_time__gte: yesterday,
            arrival_time__lt: today,
          },
          token
        );

        const todayData = todayVisits?.results || todayVisits || [];
        const yesterdayData = yesterdayVisits?.results || yesterdayVisits || [];

        // Calculate metrics
        const totalToday = todayData.length;
        const totalYesterday = yesterdayData.length;

        // Triage breakdown
        const triageBreakdown = {
          red: todayData.filter((v) => v.triage_level === 1).length,
          yellow: todayData.filter((v) => v.triage_level === 2).length,
          green: todayData.filter((v) => v.triage_level === 3).length,
        };

        // Disposition breakdown
        const dispositionBreakdown = {
          discharge: todayData.filter((v) => v.disposition === 1).length,
          admit_ipd: todayData.filter((v) => v.disposition === 2).length,
          admit_icu: todayData.filter((v) => v.disposition === 3).length,
          referral: todayData.filter((v) => v.disposition === 4).length,
          death: todayData.filter((v) => v.disposition === 5).length,
        };

        // Pending (no disposition)
        const pending = todayData.filter((v) => !v.disposition).length;

        // Average wait time (simplified - using arrival time)
        const now = new Date();
        const avgWaitTime =
          todayData.length > 0
            ? todayData.reduce((sum, visit) => {
                const arrival = new Date(visit.arrival_time);
                const waitMinutes = (now - arrival) / (1000 * 60);
                return sum + Math.max(0, waitMinutes);
              }, 0) / todayData.length
            : 0;

        setMetrics({
          totalToday,
          totalYesterday,
          triageBreakdown,
          dispositionBreakdown,
          pending,
          avgWaitTime: Math.round(avgWaitTime),
          changePercent:
            totalYesterday > 0
              ? Math.round(
                  ((totalToday - totalYesterday) / totalYesterday) * 100
                )
              : 0,
        });
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchMetrics();
  }, [token]);

  const handleChangeOption = (event) => {
    setOption(event.target.value || "daily");
  };

  if (loading) {
    return (
      <Paper sx={{ boxShadow: "0px 0px 0px", width: "100%" }}>
        <Grid container padding={"20px"}>
          <Typography>Loading...</Typography>
        </Grid>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper sx={{ boxShadow: "0px 0px 0px", width: "100%" }}>
        <Grid container padding={"20px"}>
          <Typography color="error">
            {t("common.error_loading", {
              defaultValue: "Error loading metrics",
            })}
            : {error}
          </Typography>
        </Grid>
      </Paper>
    );
  }

  if (!metrics) return null;

  return (
    <Paper sx={{ boxShadow: "0px 0px 0px", width: "100%" }}>
      <Grid container padding={"20px"}>
        <Grid
          item
          xs={11}
          md={11}
          display={"flex"}
          margin={"0px auto"}
          justifyContent={"space-between"}
          alignItems={"center"}
        >
          <Grid item>
            <Typography>
              {t("emergency.overview.title", {
                defaultValue: "Emergency Activity Overview",
              })}
            </Typography>
          </Grid>
          <Grid item>
            <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
              <InputLabel id="report-type-select-label">
                {t("overview.option", { defaultValue: "Option" })}
              </InputLabel>
              <Select
                labelId="report-type-select-label"
                id="report-type-select"
                value={option}
                onChange={handleChangeOption}
                label={t("overview.option", { defaultValue: "Option" })}
              >
                <MenuItem value="daily">
                  {t("overview.daily", { defaultValue: "Daily" })}
                </MenuItem>
                <MenuItem value="weekly">
                  {t("overview.weekly", { defaultValue: "Weekly" })}
                </MenuItem>
                <MenuItem value="monthly">
                  {t("overview.monthly", { defaultValue: "Monthly" })}
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Grid container gap={3} mt={2} justifyContent={"center"}>
          {/* Total Visits */}
          <Grid
            item
            xs={12}
            sm={3}
            lg={5}
            sx={{
              ...gridItemCenterStyle,
              backgroundColor: theme.palette.primary.main,
            }}
          >
            <LocalHospitalIcon fontSize="large" />
            <Typography
              textAlign={"center"}
              sx={{ fontWeight: "bold", margin: "5px 0px" }}
            >
              {metrics.totalToday}
            </Typography>
            <Typography textAlign={"center"}>
              {t("emergency.overview.today_visits", {
                defaultValue: "Today's Visits",
              })}
            </Typography>
          </Grid>

          {/* Pending Disposition */}
          <Grid
            item
            xs={12}
            sm={3}
            lg={5}
            sx={{
              ...gridItemCenterStyle,
              backgroundColor: theme.palette.primary.main,
            }}
          >
            <PersonIcon fontSize="large" />
            <Typography
              textAlign={"center"}
              sx={{ fontWeight: "bold", margin: "5px 0px" }}
            >
              {metrics.pending}
            </Typography>
            <Typography textAlign={"center"}>
              {t("emergency.overview.pending", {
                defaultValue: "Pending Disposition",
              })}
            </Typography>
          </Grid>

          {/* Average Wait Time */}
          <Grid
            item
            xs={12}
            sm={3}
            lg={5}
            sx={{
              ...gridItemCenterStyle,
              backgroundColor: theme.palette.primary.main,
            }}
          >
            <AccessTimeIcon fontSize="large" />
            <Typography
              textAlign={"center"}
              sx={{ fontWeight: "bold", margin: "5px 0px" }}
            >
              {metrics.avgWaitTime}m
            </Typography>
            <Typography textAlign={"center"}>
              {t("emergency.overview.avg_wait", {
                defaultValue: "Avg Wait Time",
              })}
            </Typography>
          </Grid>

          {/* Critical Cases */}
          <Grid item xs={12} sm={3} lg={5} sx={warningStyle}>
            <WarningIcon fontSize="large" />
            <Typography
              textAlign={"center"}
              sx={{
                fontWeight: "bold",
                fontSize: "1.5rem",
                margin: "5px 0px",
              }}
            >
              {metrics.triageBreakdown.red}
            </Typography>
            <Typography textAlign={"center"} sx={{ fontWeight: "bold" }}>
              {t("emergency.overview.critical", {
                defaultValue: "Critical Cases",
              })}
            </Typography>
          </Grid>

          {/* Discharged Cases */}
          <Grid item xs={12} sm={3} lg={5} sx={warningStyle}>
            <WarningIcon fontSize="large" />
            <Typography
              textAlign={"center"}
              sx={{
                fontWeight: "bold",
                fontSize: "1.5rem",
                margin: "5px 0px",
              }}
            >
              {metrics.dispositionBreakdown.discharge}
            </Typography>
            <Typography textAlign={"center"} sx={{ fontWeight: "bold" }}>
              {t("emergency.overview.discharged", {
                defaultValue: "Discharged",
              })}
            </Typography>
          </Grid>
        </Grid>
      </Grid>
    </Paper>
  );
}
